import { addEntity, addComponent } from 'bitecs';
import { Animal, AI, Production, ANIMAL_TYPES, Position, Tile, Plant, SoundLevel, Corpse, TILE_TYPES, BIOME_TYPES, animalQuery, tileQuery, plantQuery, playerQuery, corpseQuery, Velocity, Health, Hunger, Thirst } from '../world.js';
import { Renderable } from '../components.js';
import { createBee } from '../entities/bee.js';
import { createBeeEater } from '../entities/bee_eater.js';
import { updateRabbit } from '../entities/rabbit.js';
// Import spawn configuration helpers
import { shouldSpawnEntityType, canSpawnMoreOfType, updateEntityCount, getCurrentEntityCount } from '../entityConfigUtils.js';
import { TILE_SIZE, PLAYER_STATS, MAP_WIDTH, MAP_HEIGHT, ENTITY_REGISTRY, CURRENT_MODE } from '../config.js';
import { getRandomPositionNear } from '../utils/positionUtils.js';

const ANIMAL_DENSITY = 0.02; // Reduced from 0.05 for better performance
const MOVEMENT_RADIUS = 3; // Reduced from 5 to 3 for more focused movement
const FOOD_SEARCH_RADIUS = 5; // How far animals will look for food
const EATING_PROXIMITY = 0.5; // How close an animal needs to be to eat (in tiles)
const ANIMAL_TICK_RATE = 2; // Changed from 1 to 2 for less frequent updates
const MAX_ANIMALS_PER_FRAME = 25; // Reduced from 50 to 25 for better performance
const CHUNK_SIZE = 4; // Size of chunks in tiles

const AI_STATE = {
    IDLE: 0,
    MOVING: 1,
    EATING: 2,
    RESTING: 3,
    HUNTING: 4
};

// Helper function to get AI state name
export function getAIStateName(state) {
    return Object.keys(AI_STATE).find(key => AI_STATE[key] === state) || 'UNKNOWN';
}

// Initialize data structures
const animalPositions = new Map();

// Helper function to get chunk key
function getChunkKey(x, y) {
    const chunkX = Math.floor(x / (CHUNK_SIZE * TILE_SIZE));
    const chunkY = Math.floor(y / (CHUNK_SIZE * TILE_SIZE));
    return `${chunkX},${chunkY}`;
}

// Helper function to get active animals based on player position
function getActiveAnimals(world, animals) {
    const players = playerQuery(world);
    if (players.length === 0) return [];
    
    const player = players[0];
    const playerChunkX = Math.floor(Position.x[player] / (CHUNK_SIZE * TILE_SIZE));
    const playerChunkY = Math.floor(Position.y[player] / (CHUNK_SIZE * TILE_SIZE));
    
    // Filter animals to only those within active radius - reduced for performance
    return animals.filter(animal => {
        const animalChunkX = Math.floor(Animal.x[animal] / (CHUNK_SIZE * TILE_SIZE));
        const animalChunkY = Math.floor(Animal.y[animal] / (CHUNK_SIZE * TILE_SIZE));
        
        const dx = Math.abs(animalChunkX - playerChunkX);
        const dy = Math.abs(animalChunkY - playerChunkY);
        
        return dx <= 10 && dy <= 10; // Reduced from 20 to 10 chunks for better performance
    });
}

// Generate initial animals based on biomes
export function animalGenerationSystem(world) {
    // Check if animals are enabled in the current game mode
    if (!CURRENT_MODE.animals) {
        console.log("[AnimalSys] Animal generation skipped (disabled in CURRENT_MODE).");
        return world; // Exit early if animals are disabled
    }

    console.log("[SpawnDebug] animalGenerationSystem called!");
    const tiles = tileQuery(world);
    
    // --- Log Biome Counts ---
    const biomeCounts = {};
    for (const tile of tiles) {
        const biome = Tile.biome[tile];
        biomeCounts[biome] = (biomeCounts[biome] || 0) + 1;
    }
    console.log("[SpawnDebug] Map Biome Counts:", biomeCounts);
    // --- End Log Biome Counts ---

    const animalCounts = {};
    
    for (const tile of tiles) {
        if (Tile.type[tile] === TILE_TYPES.LAND) {
            // Only spawn animals in valid biomes
            const biome = Tile.biome[tile];
            const x = Tile.x[tile];
            const y = Tile.y[tile];
            
            // Random chance to spawn an animal
            if (Math.random() < ANIMAL_DENSITY) { // Back to original density check
                // Determine animal type based on biome
                let animalType;
                let chosenTypeName = 'None'; // For logging
                switch (biome) {
                    case BIOME_TYPES.PLAINS:
                        if (Math.random() < 0.1) {
                            animalType = ANIMAL_TYPES.BEE_EATER.id;
                            chosenTypeName = 'BEE_EATER';
                        } else {
                            // Spawn Rabbit, Chicken, or Goat
                            const randPlains = Math.random();
                            if (randPlains < 0.5) { animalType = ANIMAL_TYPES.RABBIT.id; chosenTypeName = 'RABBIT'; }
                            else if (randPlains < 0.8) { animalType = ANIMAL_TYPES.CHICKEN.id; chosenTypeName = 'CHICKEN'; }
                            else { animalType = ANIMAL_TYPES.GOAT.id; chosenTypeName = 'GOAT'; }
                        }
                        break;
                    case BIOME_TYPES.FOREST:
                        console.log(`[SpawnDebug] Forest tile at (${x}, ${y})`); // Log Forest tile
                        // Original Forest Logic:
                        if (Math.random() < 0.1) {
                            animalType = ANIMAL_TYPES.BEE_EATER.id;
                            chosenTypeName = 'BEE_EATER';
                        } else {
                            const randForest = Math.random();
                            console.log(`[SpawnDebug] Forest rand: ${randForest.toFixed(3)}`); // Log random number
                            if (randForest < 0.4) { animalType = ANIMAL_TYPES.WOLF.id; chosenTypeName = 'WOLF'; }
                            else if (randForest < 0.7) { animalType = ANIMAL_TYPES.RABBIT.id; chosenTypeName = 'RABBIT'; }
                            else if (randForest < 0.9) { animalType = ANIMAL_TYPES.GOAT.id; chosenTypeName = 'GOAT'; }
                            else { animalType = ANIMAL_TYPES.HAWK.id; chosenTypeName = 'HAWK'; }
                        }
                        break;
                    case BIOME_TYPES.MOUNTAINS:
                        console.log(`[SpawnDebug] Mountain tile at (${x}, ${y})`); // Log Mountain tile
                        // Original Mountain Logic:
                        const randMountain = Math.random();
                        console.log(`[SpawnDebug] Mountain rand: ${randMountain.toFixed(3)}`); // Log random number
                        animalType = randMountain < 0.7 ? ANIMAL_TYPES.GOAT.id : ANIMAL_TYPES.HAWK.id;
                        chosenTypeName = animalType === ANIMAL_TYPES.GOAT.id ? 'GOAT' : 'HAWK';
                        break;
                    default:
                        continue; // Skip other biomes
                }
                
                // Ensure an animal type was actually selected 
                if (animalType === undefined) continue;

                console.log(`[SpawnDebug] Chosen type for tile (${x}, ${y}, Biome: ${biome}): ${chosenTypeName} (ID: ${animalType})`);

                // Get animal type name from ID
                const animalTypeName = Object.entries(ANIMAL_TYPES)
                    .find(([_, type]) => type.id === animalType)?.[0];
                
                if (!animalTypeName) {
                    console.warn(`Unknown animal type ID: ${animalType}`);
                    continue;
                }

                // Log spawn checks specifically for HAWK
                if (animalTypeName === 'HAWK') {
                    const shouldSpawn = shouldSpawnEntityType(animalTypeName, 'animals');
                    const canSpawnMore = canSpawnMoreOfType(animalTypeName, 'animals');
                    console.log(`[SpawnDebug] HAWK selected. Should Spawn? ${shouldSpawn}. Can Spawn More? ${canSpawnMore}.`);
                }

                // REMOVED TEMPORARY BYPASS LOGIC

                // Check if this animal type should be spawned based on configuration
                if (!shouldSpawnEntityType(animalTypeName, 'animals')) { 
                    console.log(`Skipping spawn of ${animalTypeName} due to spawn configuration`);
                    continue;
                }
                
                // Check if we've reached the maximum count for this type
                if (!canSpawnMoreOfType(animalTypeName, 'animals')) { 
                    console.log(`Maximum count reached for ${animalTypeName}, not spawning more`);
                    continue;
                }
                
                // Removed temporary log before spawnAnimal
                // Spawn the animal
                spawnAnimal(world, x, y, animalType);
                
                // Update the count
                animalCounts[animalTypeName] = (animalCounts[animalTypeName] || 0) + 1;
                updateEntityCount(animalTypeName, 'animals', 1, true);
                // --- Log Hawk Count Update ---
                if (animalTypeName === 'HAWK') {
                    console.log(`[SpawnDebug] HAWK count updated. Current count: ${getCurrentEntityCount('HAWK', 'animals')}`);
                }
                // --- End Log Hawk Count Update ---
            }
        }
    }
    
    // Log summary of animal generation
    console.log(`[animalGenerationSystem] Animals generated:`, animalCounts);
    
    return world;
}

// Find nearest food source
export function findNearestFood(world, x, y, radius) {
    const plants = plantQuery(world);
    let nearestFood = null;
    let minDist = Infinity;
    
    for (const plant of plants) {
        // Only consider plants with food available
        if (Plant.foodAmount[plant] > 0) {
            const plantX = Plant.x[plant];
            const plantY = Plant.y[plant];
            const dx = plantX - x;
            const dy = plantY - y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist < radius && dist < minDist) {
                nearestFood = { 
                    x: plantX, 
                    y: plantY, 
                    dist,
                    plantId: plant
                };
                minDist = dist;
            }
        }
    }
    
    return nearestFood;
}

// Find nearest prey
function findNearestPrey(world, x, y, radius, predatorType) {
    const animals = animalQuery(world);
    let nearestPrey = null;
    let minDist = Infinity;
    // Get the predator's configuration from ANIMAL_TYPES based on its ID
    const predator = Object.values(ANIMAL_TYPES).find(type => type.id === predatorType);

    // Check if we found the predator config (should always be true if predatorType is valid)
    if (!predator) {
      console.warn(`[findNearestPrey] Could not find predator config for type ID: ${predatorType}`);
      return null;
    }

    // Create a Set of target *IDs* based on the preferredPrey string keys
    const preferredPreyIds = new Set();
    if (predator.preferredPrey && Array.isArray(predator.preferredPrey)) {
      predator.preferredPrey.forEach(preyKey => {
        const preyType = ANIMAL_TYPES[preyKey];
        if (preyType) {
          preferredPreyIds.add(preyType.id);
        } else {
          console.warn(`[findNearestPrey] Unknown prey key '${preyKey}' for predator type ID ${predatorType}`);
        }
      });
    }

    for (const animal of animals) {
        // Don't attack same type (wolves don't attack wolves)
        if (Animal.type[animal] === predatorType) continue;

        // Check if the current animal's ID is in the set of preferred prey IDs
        if (preferredPreyIds.has(Animal.type[animal])) {
            const dx = Animal.x[animal] - x;
            const dy = Animal.y[animal] - y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < radius && dist < minDist) {
                nearestPrey = {
                    x: Animal.x[animal],
                    y: Animal.y[animal],
                    dist,
                    animalId: animal
                };
                minDist = dist;
            }
        }
    }

    return nearestPrey;
}

// Find nearest corpse
function findNearestCorpse(world, x, y, radius) {
    const corpses = corpseQuery(world);
    let nearestCorpse = null;
    let minDist = Infinity;
    
    for (const corpse of corpses) {
        const dx = Corpse.x[corpse] - x;
        const dy = Corpse.y[corpse] - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < radius && dist < minDist && Corpse.foodAmount[corpse] > 0) {
            nearestCorpse = { 
                x: Corpse.x[corpse], 
                y: Corpse.y[corpse], 
                dist,
                corpseId: corpse
            };
            minDist = dist;
        }
    }
    
    return nearestCorpse;
}

// Find potential mate
function findMate(world, x, y, radius, type) {
    const animals = animalQuery(world);
    let nearestMate = null;
    let minDist = Infinity;
    
    for (const animal of animals) {
        if (Animal.type[animal] === type && animal !== eid) {
            const dx = Animal.x[animal] - x;
            const dy = Animal.y[animal] - y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist < radius && dist < minDist) {
                nearestMate = { x: Animal.x[animal], y: Animal.y[animal], dist };
                minDist = dist;
            }
        }
    }
    
    return nearestMate;
}

// Spawn a single animal
function spawnAnimal(world, x, y, typeId) {
    // --- Log Hawk Spawn Entry ---
    if (typeId === ANIMAL_TYPES.HAWK.id) {
        console.log(`[SpawnDebug] >>> ENTERING spawnAnimal for HAWK at (${x}, ${y})`);
    }
    // --- End Log Hawk Spawn Entry ---

    // Special case for bee eaters - use dedicated creation function
    if (typeId === ANIMAL_TYPES.BEE_EATER.id) {
        const worldX = x * TILE_SIZE + (TILE_SIZE / 2);
        const worldY = y * TILE_SIZE + (TILE_SIZE / 2);
        return createBeeEater(world, worldX, worldY);
    }

    const eid = addEntity(world);
    const animalType = Object.entries(ANIMAL_TYPES)
        .find(([_, type]) => type.id === typeId)[1];
    
    // Add components
    addComponent(world, Animal, eid);
    addComponent(world, AI, eid);
    addComponent(world, Position, eid);
    
    // Initialize animal properties with randomization
    Animal.type[eid] = typeId;
    // Convert tile coordinates to world coordinates
    const worldX = x * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    const worldY = y * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    Animal.x[eid] = worldX;
    Animal.y[eid] = worldY;
    Position.x[eid] = worldX;
    Position.y[eid] = worldY;
    Animal.age[eid] = Math.floor(Math.random() * 100); // Random starting age (0-1 days)
    Animal.weight[eid] = 10; // Start with base weight
    Animal.isMoving[eid] = 0;
    
    // Set energy based on animal type
    if (typeId === ANIMAL_TYPES.BEE_EATER.id) {
        Animal.energy[eid] = animalType.dailyEnergyRequirement * 0.3; // Start with 30% energy
    } else {
        Animal.energy[eid] = animalType.dailyEnergyRequirement; // Start with full energy
    }
    
    Animal.lastEnergyUpdate[eid] = world.tick;
    Animal.fleeTimer[eid] = 0;
    Animal.isFleeing[eid] = 0;
    
    // Initialize AI with random state and timer
    const randomState = Math.random();
    if (typeId === ANIMAL_TYPES.BEE_EATER.id) {
        // Bee eaters always start in HUNTING state since they have low energy
        AI.state[eid] = AI_STATE.HUNTING;
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * MOVEMENT_RADIUS * TILE_SIZE;
        AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
        AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
        Animal.isMoving[eid] = 1;
        AI.changeDirectionTimer[eid] = 5.0;  // 5 seconds before changing direction
        AI.changeDirectionInterval[eid] = 5; // Change direction every 5 seconds
    } else {
        // Other animals can use IDLE state
        if (randomState < 0.3) {
            AI.state[eid] = AI_STATE.IDLE;
        } else if (randomState < 0.6) {
            AI.state[eid] = AI_STATE.MOVING;
            // Random movement target
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * MOVEMENT_RADIUS * TILE_SIZE;
            AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
            AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
            Animal.isMoving[eid] = 1;
        } else {
            AI.state[eid] = AI_STATE.IDLE;
        }
    }
    
    // Ensure AI state is always valid
    if (AI.state[eid] === undefined || AI.state[eid] === null) {
        AI.state[eid] = AI_STATE.MOVING;  // Default to MOVING instead of IDLE
    }
    
    AI.timer[eid] = Math.floor(Math.random() * 1000);
    AI.speed[eid] = animalType.speed; // Initialize AI speed
    
    // Add production component for animals that produce items
    if (animalType.product) {
        addComponent(world, Production, eid);
        Production.timer[eid] = Math.floor(Math.random() * animalType.product.baseTime); // Randomize production timer
        Production.baseTime[eid] = animalType.product.baseTime;
    }
    
    console.log(`Spawned ${animalType.name} at (${x}, ${y}) with initial state: ${getAIStateName(AI.state[eid])}`);
}

// Create a corpse when an animal dies
export function createCorpse(world, x, y, type) {
    console.log('Creating corpse at', x, y, 'for type', type);
    const eid = addEntity(world);
    addComponent(world, Corpse, eid);
    
    Corpse.type[eid] = type;
    Corpse.x[eid] = x;
    Corpse.y[eid] = y;
    Corpse.foodAmount[eid] = 100; // All corpses start with 100 food
    Corpse.decayRate[eid] = 0.1; // All corpses decay at the same rate
    Corpse.age[eid] = 0;
    
    // console.log(`Created corpse at (${x}, ${y})`);
}

// Update animal behavior
export function animalBehaviorSystem(world, delta) {
    // Ensure delta is a valid number, provide default if not
    let deltaTimeSeconds = (typeof delta === 'number' && !isNaN(delta) ? delta : 16.67) / 1000;
    if (isNaN(deltaTimeSeconds) || deltaTimeSeconds <= 0) {
        console.warn(`[AnimalSys] Invalid delta time received: ${delta}. Using default.`);
        deltaTimeSeconds = 16.67 / 1000; // Default to ~60 FPS frame time
    }

    const allAnimals = animalQuery(world); // Get all animals first
    const players = playerQuery(world); // Get player for position

    if (players.length === 0) {
        // console.log("[AnimalSys] No player found, skipping animal behavior.");
        return world;
    }
    const player = players[0];
    const playerX = Position.x[player];
    const playerY = Position.y[player];
    const playerChunkX = Math.floor(playerX / (TILE_SIZE * CHUNK_SIZE));
    const playerChunkY = Math.floor(playerY / (TILE_SIZE * CHUNK_SIZE));
    const activeRadiusInChunks = 5; // Process animals within 5 chunks of the player
    const minChunkX = playerChunkX - activeRadiusInChunks;
    const maxChunkX = playerChunkX + activeRadiusInChunks;
    const minChunkY = playerChunkY - activeRadiusInChunks;
    const maxChunkY = playerChunkY + activeRadiusInChunks;

    // Filter animals based on proximity to player chunk and basic status
    const activeAnimals = allAnimals.filter(eid => {
        // Basic status checks
        if (Animal.energy[eid] <= 0) return false;
        const animalType = Object.values(ANIMAL_TYPES).find(type => type.id === Animal.type[eid]);
        if (!animalType || Animal.age[eid] >= animalType.maxAge) return false;
        if (Animal.type[eid] === ANIMAL_TYPES.BEE.id && Animal.energy[eid] <= 0) return false;

        // Chunk check
        const animalChunkX = Math.floor(Position.x[eid] / (TILE_SIZE * CHUNK_SIZE)); // Use Position component
        const animalChunkY = Math.floor(Position.y[eid] / (TILE_SIZE * CHUNK_SIZE)); // Use Position component
        return animalChunkX >= minChunkX && animalChunkX <= maxChunkX &&
               animalChunkY >= minChunkY && animalChunkY <= maxChunkY;
    });
    
    // Process animals in batches to maintain performance
    const BATCH_SIZE = 10;
    const MAX_TIME_PER_FRAME = 16; // 16ms = ~60fps
    const now = performance.now();
    
    // const animalsToProcess = Math.min(activeAnimals.length, MAX_ANIMALS_PER_FRAME); // Limit processing further if needed
    const animalsToProcess = activeAnimals.length; // Process all animals within active radius for now
    
    // console.log('[ANIMAL_SYSTEM] Processing', animalsToProcess, 'animals within active radius out of', allAnimals.length, 'total');
    
    for (let i = 0; i < animalsToProcess; i += BATCH_SIZE) {
        const endIdx = Math.min(i + BATCH_SIZE, animalsToProcess);
        
        for (let j = i; j < endIdx; j++) {
            const eid = activeAnimals[j];
            const animalType = Object.values(ANIMAL_TYPES).find(
                type => type.id === Animal.type[eid]
            );
            
            console.log('[ANIMAL_SYSTEM] Processing animal:', eid, 'type:', animalType?.name, 'state:', getAIStateName(AI.state[eid]));
            
            // Skip bee eaters as they're handled by the aiSystem
            if (Animal.type[eid] === ANIMAL_TYPES.BEE_EATER.id) {
                console.log('[ANIMAL_SYSTEM] Skipping bee eater');
                continue;
            }
            
            // Use rabbit-specific update for rabbits and goats
            if (Animal.type[eid] === ANIMAL_TYPES.RABBIT.id || Animal.type[eid] === ANIMAL_TYPES.GOAT.id) {
                console.log('[ANIMAL_SYSTEM] Calling updateRabbit for:', eid, 'with delta:', deltaTimeSeconds);
                updateRabbit(world, eid, deltaTimeSeconds); // Pass scaled delta
                continue;
            }
            
            // Update age and energy
            if (world.tick % 60 === 0) {
                Animal.age[eid]++;
            }
            
            // Update energy (daily requirement)
            const ticksPerDay = 1000;
            const energyPerTick = animalType.dailyEnergyRequirement / ticksPerDay;
            
            // Check if animal has reached max age or died of starvation
            if (Animal.age[eid] >= animalType.maxAge || Animal.energy[eid] <= 0) {
                createCorpse(world, Animal.x[eid], Animal.y[eid], Animal.type[eid]);
                continue;
            }
            
            // Update AI state
            switch (AI.state[eid]) {
                /* // Commenting out HUNTING case in animalBehaviorSystem as it seems incorrect
                case AI_STATE.HUNTING:
                    // Check if energy is too low to continue hunting
                    if (Animal.energy[eid] < animalType.dailyEnergyRequirement * 0.4) {
                        AI.state[eid] = AI_STATE.RESTING;
                        Animal.isMoving[eid] = 0;
                        break;
                    }

                    // Look for bees
                    const nearestBee = findNearestBee(world, Animal.x[eid], Animal.y[eid]);
                    if (nearestBee) {
                        const beeX = Position.x[nearestBee];
                        const beeY = Position.y[nearestBee];
                        AI.targetX[eid] = beeX;
                        AI.targetY[eid] = beeY;
                        
                        // Move towards bee with increased speed
                        const huntDx = AI.targetX[eid] - Animal.x[eid];
                        const huntDy = AI.targetY[eid] - Animal.y[eid];
                        const huntDist = Math.sqrt(huntDx * huntDx + huntDy * huntDy);
                        
                        if (huntDist < EATING_PROXIMITY) {
                            // Eat the bee
                            eatBee(world, eid, nearestBee);
                            AI.state[eid] = AI_STATE.EATING;
                            AI.timer[eid] = 1.0; // 1 second eating animation
                        } else {
                            // Move faster when hunting
                            const baseSpeed = animalType.speed * 1.5; // 50% faster when hunting
                            const moveX = (huntDx / huntDist) * baseSpeed;
                            const moveY = (huntDy / huntDist) * baseSpeed;
                            
                            Animal.x[eid] += moveX;
                            Animal.y[eid] += moveY;
                            Animal.isMoving[eid] = 1;
                        }
                    } else {
                        // No bees found, go back to MOVING state
                        AI.state[eid] = AI_STATE.MOVING;
                        const angle = Math.random() * Math.PI * 2;
                        const distance = Math.random() * MOVEMENT_RADIUS * TILE_SIZE;
                        AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
                        AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
                        Animal.isMoving[eid] = 1;
                    }
                    break;
                */

                case AI_STATE.IDLE:
                    // Check if energy is too low to do anything
                    if (Animal.energy[eid] < animalType.dailyEnergyRequirement * 0.4) {
                        AI.state[eid] = AI_STATE.RESTING;
                        break;
                    }
                    
                    // Only check for food if not fleeing
                    if (!Animal.isFleeing[eid] && Animal.energy[eid] < animalType.dailyEnergyRequirement * 0.6) {
                        if (animalType.isCarnivore) {
                            // Look for corpses or prey
                            const corpse = findNearestCorpse(world, Animal.x[eid], Animal.y[eid], FOOD_SEARCH_RADIUS);
                            if (corpse) {
                                AI.targetX[eid] = corpse.x;
                                AI.targetY[eid] = corpse.y;
                                AI.state[eid] = AI_STATE.MOVING;
                                Animal.isMoving[eid] = 1;
                                AI.timer[eid] = corpse.corpseId;
                            } else {
                                const prey = findNearestPrey(world, Animal.x[eid], Animal.y[eid], animalType.huntRadius, Animal.type[eid]);
                                if (prey) {
                                    AI.targetX[eid] = prey.x;
                                    AI.targetY[eid] = prey.y;
                                    AI.state[eid] = AI_STATE.MOVING;
                                    Animal.isMoving[eid] = 1;
                                    AI.timer[eid] = prey.animalId;
                                }
                            }
                        } else {
                            // Look for plants
                            const food = findNearestFood(world, Animal.x[eid], Animal.y[eid], FOOD_SEARCH_RADIUS);
                            if (food) {
                                AI.targetX[eid] = food.x;
                                AI.targetY[eid] = food.y;
                                AI.state[eid] = AI_STATE.MOVING;
                                Animal.isMoving[eid] = 1;
                                AI.timer[eid] = food.plantId;
                            }
                        }
                    } else if (Math.random() < 0.005) {
                        // Random movement
                        const angle = Math.random() * Math.PI * 2;
                        const distance = Math.random() * MOVEMENT_RADIUS;
                        AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
                        AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
                        AI.state[eid] = AI_STATE.MOVING;
                        Animal.isMoving[eid] = 1;
                    }
                    break;
                    
                case AI_STATE.RESTING:
                    // Regain energy while resting
                    Animal.energy[eid] = Math.min(animalType.dailyEnergyRequirement, 
                        Animal.energy[eid] + energyPerTick * 2);
                    
                    // Check if rested enough
                    if (Animal.energy[eid] >= animalType.dailyEnergyRequirement * 0.7) {
                        if (Animal.type[eid] === ANIMAL_TYPES.BEE_EATER.id) {
                            // Bee eaters go back to MOVING state
                            AI.state[eid] = AI_STATE.MOVING;
                            const angle = Math.random() * Math.PI * 2;
                            const distance = Math.random() * MOVEMENT_RADIUS * TILE_SIZE;
                            AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
                            AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
                            Animal.isMoving[eid] = 1;
                        } else {
                            // Other animals go to IDLE state
                            AI.state[eid] = AI_STATE.IDLE;
                        }
                    }
                    break;
                    
                case AI_STATE.MOVING:
                    // Check if energy is too low to continue moving
                    if (Animal.energy[eid] < animalType.dailyEnergyRequirement * 0.4) {
                        AI.state[eid] = AI_STATE.RESTING;
                        Animal.isMoving[eid] = 0;
                        break;
                    }

                    // Move towards target
                    const moveDx = AI.targetX[eid] - Animal.x[eid];
                    const moveDy = AI.targetY[eid] - Animal.y[eid];
                    const moveDist = Math.sqrt(moveDx * moveDx + moveDy * moveDy);
                    
                    if (moveDist < EATING_PROXIMITY) {
                        // Reached target
                        if (animalType.isCarnivore) {
                            const corpseId = AI.timer[eid];
                            if (corpseId !== undefined && Corpse.foodAmount[corpseId] > 0) {
                                const foodEaten = Math.min(10, Corpse.foodAmount[corpseId]);
                                Corpse.foodAmount[corpseId] -= foodEaten;
                                const energyGained = foodEaten * animalType.energyPerFood * animalType.energyEfficiency;
                                Animal.energy[eid] = Math.min(animalType.dailyEnergyRequirement, 
                                    Animal.energy[eid] + energyGained);
                            }
                        } else {
                            const plantId = AI.timer[eid];
                            if (plantId !== undefined && Plant.foodAmount[plantId] > 0) {
                                const energyNeeded = animalType.dailyEnergyRequirement - Animal.energy[eid];
                                const foodNeeded = Math.ceil(energyNeeded / (animalType.energyPerFood * animalType.energyEfficiency));
                                const foodAvailable = Plant.foodAmount[plantId];
                                const foodEaten = Math.min(foodNeeded, foodAvailable, 10);
                                
                                if (foodEaten > 0) {
                                    Plant.foodAmount[plantId] -= foodEaten;
                                    const energyGained = foodEaten * animalType.energyPerFood * animalType.energyEfficiency;
                                    Animal.energy[eid] = Math.min(animalType.dailyEnergyRequirement, 
                                        Animal.energy[eid] + energyGained);
                                }
                            }
                        }
                        
                        // Set new random target for bee eaters, otherwise go to IDLE
                        if (Animal.type[eid] === ANIMAL_TYPES.BEE_EATER.id) {
                            const angle = Math.random() * Math.PI * 2;
                            const distance = Math.random() * MOVEMENT_RADIUS * TILE_SIZE;
                            AI.targetX[eid] = Animal.x[eid] + Math.cos(angle) * distance;
                            AI.targetY[eid] = Animal.y[eid] + Math.sin(angle) * distance;
                            Animal.isMoving[eid] = 1;
                        } else {
                            AI.state[eid] = AI_STATE.IDLE;
                            Animal.isMoving[eid] = 0;
                        }
                    } else {
                        // Calculate movement with smooth acceleration and deceleration
                        const baseSpeed = animalType.speed * (Animal.isFleeing[eid] ? animalType.fleeSpeed : 1);
                        const acceleration = 0.1;
                        const deceleration = 0.2;
                        
                        const energyFactor = Animal.energy[eid] / animalType.dailyEnergyRequirement;
                        const targetSpeed = baseSpeed * (1 - Math.min(moveDist / FOOD_SEARCH_RADIUS, 0.5)) * energyFactor;
                        
                        const currentSpeed = Animal.isMoving[eid] ? 
                            Math.min(targetSpeed, Animal.isMoving[eid] + acceleration) :
                            Math.max(0, Animal.isMoving[eid] - deceleration);
                        
                        Animal.isMoving[eid] = currentSpeed;
                        
                        const moveX = (moveDx / moveDist) * currentSpeed;
                        const moveY = (moveDy / moveDist) * currentSpeed;
                        
                        Animal.x[eid] += moveX;
                        Animal.y[eid] += moveY;
                    }
                    break;
            }
            
            // Handle production (e.g., egg laying)
            if (Production.timer[eid] !== undefined && animalType.product) {
                Production.timer[eid]++;
                if (Production.timer[eid] >= Production.baseTime[eid]) {
                    Production.timer[eid] = 0;
                }
            }
            
            // Update AI timer
            AI.timer[eid]++;
        }
        
        // If processing is taking too long, yield to next frame
        if (performance.now() - now > MAX_TIME_PER_FRAME) {
            break;
        }
    }
    
    // Update corpses
    const corpses = corpseQuery(world);
    for (const corpse of corpses) {
        Corpse.age[corpse]++;
        Corpse.foodAmount[corpse] = Math.max(0, Corpse.foodAmount[corpse] - Corpse.decayRate[corpse]);
    }
    
    return world;
} 